import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  DialogActions,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import { HttpStatusCode, isAxiosError } from "axios";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { getCompletePropertyRows } from "@/utils/propertyRows";
import {
  buildRequestFingerprint,
  clearPendingIdempotencyKey,
  getPendingIdempotencyKey,
} from "@/features/map/utils/idempotencyKey";
import { useApiClient } from "@/hooks/useApiClient";
import { userDataContext } from "@/hooks/useUserData";
import { READ_ONLY_TOOLTIP_MESSAGE } from "@/utils/permissions";
import {
  calculateEstimate,
  getStandardDelivery,
} from "../utils/calculateEstimate";
import {
  type DeliverySchedule,
  getDeliverySchedule,
  hasQuoteChanged,
} from "../utils/deliverySchedule";
import { useResearchAccess } from "../hooks/useResearchAccess";
import { classifyArea } from "../utils/classifyArea";
import { buildFullAddress, splitPrefectureFromAddress } from "../utils/address";
import {
  normalizeResearchRequestData,
  RealEstateResearchRequestAPI,
} from "../api/realEstateResearchRequestAPI";
import {
  createDefaultResearchRequestFormData,
  realEstateResearchRequestSchema,
} from "../schema/realEstateResearchRequestSchema";
import {
  type AreaCode,
  type EstimateResult,
  type ResearchRequestFormData,
  type ResearchRequestInitialPropertyData,
} from "../types";
import { ResearchDetailsStep } from "./ResearchDetailsStep";
import { RequesterDetailsStep } from "./RequesterDetailsStep";
import { RequestConfirmationStep } from "./RequestConfirmationStep";
import { RequestSubmitConfirmDialog } from "./RequestSubmitConfirmDialog";
import { SubmissionComplete } from "./SubmissionComplete";
import { ResearchRequestManualLink } from "./ResearchRequestManualLink";
import {
  HeaderBox,
  StyledDialog,
  StyledDialogContent,
} from "@/features/realEstateDocumentFetch/components/styles";
import {
  footerButtonSx,
  footerSecondaryButtonSx,
  StepIndicator,
} from "./formLayout";

interface RealEstateResearchRequestWizardProps {
  open: boolean;
  onClose?: () => void;
  initialPropertyData?: ResearchRequestInitialPropertyData;
}

const steps = ["物件・調査内容", "依頼者・関連情報", "内容・見積確認"];

const stepOneFields: Array<keyof ResearchRequestFormData> = [
  "addressPrefecture",
  "address",
  "chibanList",
  "kaokuNumberList",
  "requestTypes",
  "specialOptions",
  "registryAdjacentKind",
  "customDocuments",
  "desiredDeliveryDate",
];

// 15時の締め（当日受付か翌営業日受付か）と日付の変わり目を、画面を開いたままでも反映する
const NOW_REFRESH_MS = 60 * 1000;

const stepTwoFields: Array<keyof ResearchRequestFormData> = [
  "requesterName",
  "requesterEmail",
  "requesterPhone",
  "postalAddress",
  "notes",
];

/** 依頼 API の 400 応答（{項目: [メッセージ]}）から、最初のメッセージを取り出す */
const getFirstErrorMessage = (data: unknown): string => {
  const first: unknown =
    data && typeof data === "object" ? Object.values(data)[0] : undefined;
  const message: unknown = Array.isArray(first) ? first[0] : first;
  return typeof message === "string" ? message : "入力内容に誤りがあります";
};

export const RealEstateResearchRequestWizard: React.FC<
  RealEstateResearchRequestWizardProps
> = ({ open, onClose, initialPropertyData }) => {
  const { data: userData } = useContext(userDataContext);
  const isReadOnly = useReadOnlyUser();
  const isMobile = useIsMobile();
  const initialPropertyDataRef = useRef(initialPropertyData);
  const [initialFormData] = useState(() =>
    createDefaultResearchRequestFormData(initialPropertyDataRef.current)
  );
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [files, setFiles] = useState<File[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // 確認ダイアログを開いた時点の納期と見積。表示は1分ごとに更新されるため、送る直前の値はこれと比べる
  const confirmedQuoteRef = useRef<{
    delivery: DeliverySchedule | null;
    estimate: EstimateResult;
  } | null>(null);
  const [completedRequestId, setCompletedRequestId] = useState("");
  const { apiClient } = useApiClient();
  const form = useForm<ResearchRequestFormData>({
    mode: "onTouched",
    defaultValues: initialFormData,
    resolver: zodResolver(realEstateResearchRequestSchema),
  });
  const { control, formState, handleSubmit, reset, trigger } = form;
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, NOW_REFRESH_MS);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const addressPrefecture = useWatch({ control, name: "addressPrefecture" });
  const address = useWatch({ control, name: "address" });
  const isIsland = useWatch({ control, name: "isIsland" });
  const requestTypes = useWatch({ control, name: "requestTypes" });
  const onsitePhoto = useWatch({ control, name: "onsitePhoto" });
  const specialOptions = useWatch({ control, name: "specialOptions" });
  const deadlineShortening = useWatch({
    control,
    name: "deadlineShortening",
  });
  const needsPostalDelivery = useWatch({
    control,
    name: "needsPostalDelivery",
  });
  const chibanList = useWatch({ control, name: "chibanList" });
  const specifyDeliveryDate = useWatch({
    control,
    name: "specifyDeliveryDate",
  });
  const desiredDeliveryDate = useWatch({
    control,
    name: "desiredDeliveryDate",
  });
  // 地番の土地が見つからないときは、対象住所の位置で最寄駅を判定する
  const accessState = useResearchAccess(
    apiClient,
    chibanList,
    buildFullAddress(addressPrefecture, address)
  );

  const detectedArea = useMemo<AreaCode | null>(() => {
    if (!addressPrefecture || !address.trim()) return null;
    const addressBody = splitPrefectureFromAddress(address).address;
    return classifyArea(
      { prefName: addressPrefecture, location: addressBody },
      isIsland
    );
  }, [address, addressPrefecture, isIsland]);

  const standardDelivery =
    detectedArea && requestTypes.length > 0
      ? getStandardDelivery(requestTypes, detectedArea)
      : null;

  // 納期は時刻（15時の締め）で変わるため、表示用と送信の直前とで同じ計算を使う
  const buildQuote = useCallback(
    (
      at: Date
    ): {
      delivery: DeliverySchedule | null;
      estimate: EstimateResult | null;
    } => {
      const delivery = standardDelivery
        ? getDeliverySchedule({
            standardDelivery,
            now: at,
            deadlineShortening,
            specifyDeliveryDate,
            desiredDeliveryDate,
          })
        : null;
      const estimate =
        detectedArea && requestTypes.length > 0
          ? calculateEstimate({
              area: detectedArea,
              requestTypes,
              onsitePhoto,
              specialOptions,
              deadlineShortening: delivery?.shortening ?? 0,
              deliveryQuoteRequired: delivery?.quoteRequired ?? false,
              access: accessState.access,
              needsPostalDelivery,
            })
          : null;
      return { delivery, estimate };
    },
    [
      accessState.access,
      deadlineShortening,
      desiredDeliveryDate,
      detectedArea,
      needsPostalDelivery,
      onsitePhoto,
      requestTypes,
      specialOptions,
      specifyDeliveryDate,
      standardDelivery,
    ]
  );
  const { delivery, estimate } = useMemo(
    () => buildQuote(now),
    [buildQuote, now]
  );

  const loginUser = useMemo(
    () => ({
      name: `${userData.lastName} ${userData.firstName}`.trim(),
      email: userData.email,
    }),
    [userData.email, userData.firstName, userData.lastName]
  );

  const handleNext = async (): Promise<void> => {
    const fields = step === 1 ? stepOneFields : stepTwoFields;
    if (await trigger(fields)) {
      setStep((current) => (current === 1 ? 2 : 3));
    }
  };

  // 確定時に全体の検証で落ちたとき（日付をまたいで希望納期が過去になった等）、エラーのあるページへ戻す
  const backToStepWithError = (targetStep: 1 | 2, message: string): void => {
    toast.error(message);
    setConfirmOpen(false);
    setStep(targetStep);
  };

  const submit = handleSubmit(
    async (data) => {
      if (isReadOnly) {
        toast.error(READ_ONLY_TOOLTIP_MESSAGE);
        return;
      }
      if (!estimate) {
        backToStepWithError(
          1,
          "概算を計算できませんでした。物件・調査内容を確認してください"
        );
        return;
      }
      if (accessState.loading) {
        toast.info(
          "最寄駅を確認しています。少し待ってから、もう一度お試しください"
        );
        return;
      }
      try {
        // 再送キーは依頼内容（添付の中身を含む）ごとにタブ内で保持する。応答が失われて画面を閉じ、
        // 同じ内容で依頼し直しても同じキーを送り、サーバーが重複して投稿しない（役所資料取得依頼と同じ）
        const fingerprint = await buildRequestFingerprint({
          ...normalizeResearchRequestData(data),
          relatedFiles: files,
        });
        // 確認画面を開いたまま15時の締めや日付をまたぐと、納期と金額が変わる。
        // 添付の指紋の計算にも時間がかかるため、送る直前の時刻で計算し直し、変わったら送らずに見直してもらう
        const submittedAt = new Date();
        const latest = buildQuote(submittedAt);
        const confirmed = confirmedQuoteRef.current ?? { delivery, estimate };
        if (
          !latest.estimate ||
          hasQuoteChanged(confirmed, {
            delivery: latest.delivery,
            estimate: latest.estimate,
          })
        ) {
          setNow(submittedAt);
          setConfirmOpen(false);
          toast.info(
            "受付の日時が変わったため、納期と見積を更新しました。内容を確認してから、もう一度お送りください"
          );
          return;
        }
        const { requestId } = await new RealEstateResearchRequestAPI(
          apiClient
        ).submitRequest({
          data,
          files,
          estimate: latest.estimate,
          delivery: latest.delivery,
          idempotencyKey: getPendingIdempotencyKey(fingerprint),
        });
        clearPendingIdempotencyKey(fingerprint);
        setCompletedRequestId(requestId);
        setConfirmOpen(false);
      } catch (error) {
        // 入力の誤りやファイルの中身の不一致は、再送しても通らないため理由を出す
        if (
          isAxiosError(error) &&
          error.response?.status === HttpStatusCode.BadRequest
        ) {
          toast.error(
            `入力内容を確認してください: ${getFirstErrorMessage(
              error.response.data
            )}`
          );
          return;
        }
        if (
          isAxiosError(error) &&
          error.response?.status === HttpStatusCode.Conflict
        ) {
          toast.error(
            "直前の依頼を処理しています。少し待ってから、もう一度お試しください"
          );
          return;
        }
        // 確認ダイアログは開いたままにし、入力を残したまま再送できるようにする
        Sentry.captureException(error);
        toast.error(
          "依頼を送信できませんでした。時間をおいて、もう一度お試しください"
        );
      }
    },
    (errors) => {
      const hasStepOneError = stepOneFields.some((field) => field in errors);
      backToStepWithError(
        hasStepOneError ? 1 : 2,
        `入力内容に誤りがあります。${
          steps[hasStepOneError ? 0 : 1]
        }を確認してください`
      );
    }
  );

  const handleReset = (): void => {
    reset(createDefaultResearchRequestFormData(initialPropertyDataRef.current));
    setFiles([]);
    setCompletedRequestId("");
    setConfirmOpen(false);
    setStep(1);
  };

  const content = (): React.ReactNode => {
    if (completedRequestId) {
      return (
        <SubmissionComplete
          requestId={completedRequestId}
          onReset={handleReset}
          onClose={onClose}
        />
      );
    }
    if (step === 1) {
      return (
        <ResearchDetailsStep
          form={form}
          detectedArea={detectedArea}
          standardDelivery={standardDelivery}
          delivery={delivery}
          access={accessState.access}
          accessLoading={accessState.loading}
          hasChiban={getCompletePropertyRows(chibanList).length > 0}
        />
      );
    }
    if (step === 2) {
      return (
        <RequesterDetailsStep
          form={form}
          files={files}
          onFilesChange={setFiles}
          loginUser={loginUser}
        />
      );
    }
    if (!estimate) return null;
    return (
      <RequestConfirmationStep
        data={form.getValues()}
        files={files}
        estimate={estimate}
        delivery={delivery}
        accessLoading={accessState.loading}
      />
    );
  };

  return (
    <StyledDialog
      open={open}
      // 入力中の内容が消えないよう、背景のクリックと Esc では閉じない（閉じるボタンだけで閉じる）
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose?.();
      }}
      fullScreen={isMobile}
      maxWidth="xl"
      aria-labelledby="research-request-title"
    >
      <StyledDialogContent>
        <HeaderBox>
          <Typography
            id="research-request-title"
            variant="h6"
            component="h2"
            fontWeight="bold"
            sx={{
              fontSize: { xs: "1.1rem", sm: "1.25rem", md: "1.5rem" },
              lineHeight: 1.2,
            }}
          >
            不動産調査依頼
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ResearchRequestManualLink />
            {onClose ? (
              <IconButton
                aria-label="閉じる"
                onClick={onClose}
                size="small"
                sx={{ "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}
              >
                <CloseIcon sx={{ fontSize: { xs: "20px", sm: "24px" } }} />
              </IconButton>
            ) : null}
          </Box>
        </HeaderBox>

        {!completedRequestId ? (
          <StepIndicator step={step} labels={steps} />
        ) : null}

        {content()}
      </StyledDialogContent>

      {/* 長い入力でも最後までスクロールせずに進めるよう、ボタンはスクロールの外に置く */}
      {!completedRequestId ? (
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            borderTop: "1px solid #e9ecef",
            gap: 1.5,
            flexDirection: { xs: "column-reverse", sm: "row" },
            justifyContent: "center",
            "& > :not(style) ~ :not(style)": { ml: 0 },
          }}
        >
          {step > 1 ? (
            <Button
              variant="outlined"
              onClick={() => {
                setStep((current) => (current === 3 ? 2 : 1));
              }}
              sx={footerSecondaryButtonSx}
            >
              ← 戻る
            </Button>
          ) : onClose ? (
            <Button
              variant="outlined"
              onClick={onClose}
              sx={footerSecondaryButtonSx}
            >
              キャンセル
            </Button>
          ) : null}
          {step < 3 ? (
            <Button
              variant="contained"
              onClick={() => {
                handleNext().catch((error: unknown) => {
                  Sentry.captureException(error);
                });
              }}
              sx={footerButtonSx}
            >
              次へ
            </Button>
          ) : (
            // 閲覧専用アカウントは入力・見積確認まではできるが、依頼はできない
            // SP では「次へ」「戻る」と同じく横いっぱいにする（ツールチップの枠が縮まないように）
            <ReadOnlyTooltip isReadOnly={isReadOnly} fullWidth={isMobile}>
              <Button
                variant="contained"
                disabled={isReadOnly}
                onClick={() => {
                  confirmedQuoteRef.current = estimate
                    ? { delivery, estimate }
                    : null;
                  setConfirmOpen(true);
                }}
                sx={footerButtonSx}
              >
                依頼する
              </Button>
            </ReadOnlyTooltip>
          )}
        </DialogActions>
      ) : null}

      <RequestSubmitConfirmDialog
        open={confirmOpen}
        isSubmitting={formState.isSubmitting}
        onBack={() => {
          setConfirmOpen(false);
        }}
        onConfirm={() => {
          void submit();
        }}
      />
    </StyledDialog>
  );
};
